
import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * It reacts to variant changes without unnecessarily replacing the full
 * price container, preserving BEAST's converted markup when prices match.
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #lastServerPriceHtml = null;
  #updateVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    // Record original server-rendered markup. Currency apps can later change
    // the live displayed text without changing the underlying variant price.
    if (this.#lastServerPriceHtml === null) {
      this.#lastServerPriceHtml = this.refs.priceContainer?.innerHTML ?? null;
    }

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#updateVersion += 1;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price only if server-rendered
   * pricing markup has changed.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const updateVersion = ++this.#updateVersion;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || updateVersion !== this.#updateVersion || !this.isConnected) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          const nextServerPriceHtml = newPrice.innerHTML;

          // Most size variants have identical prices. When markup is the same,
          // do not touch the live DOM (or BEAST's already-converted numbers).
          if (newProduct || nextServerPriceHtml !== this.#lastServerPriceHtml) {
            // Horizon's morph utility updates only differing DOM nodes rather
            // than removing and rebuilding the entire price wrapper.
            morph(priceContainer, newPrice);
          }

          this.#lastServerPriceHtml = nextServerPriceHtml;
        }

        // Preserve Horizon's original volume-pricing-note behavior.
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          this.refs.priceContainer?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else if (volumePricingNote.innerHTML !== newNote.innerHTML) {
          morph(volumePricingNote, newNote);
        }

        // Keep installment/payment terms synchronized with the selected size.
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
